import {
  getCheckoutData,
  getCorrelationId,
  getDcfExperience,
  getDpaLocale,
  getIsFeatureToggleEnabled,
  getIsSPCSupported,
  getIsVisaSRCi,
  getLaunchData,
  getMaskedCards,
  getMerchantCountryCode,
  getProofState,
  getSelectedCardId,
  getSessionId,
  getVppsOrigin,
  isTransactionFidoEligible
} from 'communicator/store/selectors';
import { AuthenticationDecision, WindowType } from 'types/data';
import { AuthenticationMethod } from 'types/emvco';
import {
  BankAppLaunchData,
  CheckoutLaunchData,
  IssuerLaunchData,
  FIDOAppLaunchData,
  ThreeDsLaunchData,
  VPPLaunchData
} from 'types/visa-sdk/checkout';

const FIDO = 'fido';
const CVV = 'cvv';
const APPAuth = 'appAuth';
const OTP = 'otp';
const THREEDS = 'threeds';

export function dcfLaunchHandler(launchDcf: (launchData: CheckoutLaunchData) => void) {
  return () => {
    const launchData: CheckoutLaunchData = getLaunchData();
    //FIDO popup flow - Determine FIDO flow if user sees a CVV fallback due to 3DS failure
    const checkoutData = getCheckoutData();

    if (
      launchData.integrated &&
      checkoutData?.authenticationContext &&
      checkoutData.authenticationMethod
    ) {
      //Set the Managed Authentication Flow
      launchData.isManagedAuthFlow = true;
      //Determine if we are in FIDO flow
      launchData.isFIDOEligible = isTransactionFidoEligible();
    }

    launchDcf(launchData);
  };
}

export function threeDsLaunchHandler(launchThreeDsWindow: (launchData: ThreeDsLaunchData) => void) {
  return (idvUrl: string, windowType?: WindowType) => {
    const isVisaSRCi = getIsVisaSRCi();
    const dcfExperience = getDcfExperience();
    const isIntegrated = dcfExperience === 'integrated';
    const launchThreeDsData: ThreeDsLaunchData = {
      correlationId: getCorrelationId(),
      handleNoLoader: getIsFeatureToggleEnabled('enableLoaderRemoval'),
      isCtpDomain: getIsFeatureToggleEnabled('stepupAppDomain'),
      isFIDOEligible: isTransactionFidoEligible(),
      isIntegrated,
      isRenderedOnStepupApp: getIsFeatureToggleEnabled('stepupAppThreeds'),
      isSPCSupported: getIsSPCSupported(),
      isVisaSRCi,
      url: idvUrl,
      windowType
    };

    if (getIsFeatureToggleEnabled('enableNonDcfCvv')) {
      launchThreeDsData.authDecision = THREEDS;
    }

    launchThreeDsWindow(launchThreeDsData);
  };
}

export function cvvLaunchHandler(launchNonDcfCvvWindow: (launchData: IssuerLaunchData) => void) {
  return (launchWithoutStepLoad?: boolean) => {
    /* START GENAI@GHCOPILOT */
    const panLastFour = getMaskedCards()?.filter(
      card => card.srcDigitalCardId === getSelectedCardId()
    )[0].panLastFour;
    /* END GENAI@GHCOPILOT */

    launchNonDcfCvvWindow({
      authDecision: CVV,
      correlationId: getCorrelationId(),
      dpaLocale: getDpaLocale(),
      handleNoLoader: getIsFeatureToggleEnabled('enableLoaderRemoval'),
      isCtpDomain: getIsFeatureToggleEnabled('stepupAppDomain'),
      isFidoEligible: isTransactionFidoEligible(),
      launchWithoutStepLoad,
      merchantCountryCode: getMerchantCountryCode(),
      panLastFour
    });
  };
}

export function srcOrchestrateBankAppHandler(
  launchBankApp: (authenticationMethod: AuthenticationMethod, launchData: BankAppLaunchData) => void
) {
  /* START GENAI@GHCOPILOT */
  return (authenticationMethod: AuthenticationMethod) => {
    launchBankApp(authenticationMethod, {
      authDecision: APPAuth,
      correlationId: getCorrelationId(),
      dpaLocale: getDpaLocale(),
      isCtpDomain: getIsFeatureToggleEnabled('stepupAppDomain'),
      isFidoEligible: isTransactionFidoEligible(),
      merchantCountryCode: getMerchantCountryCode()
    });
  };
  /* END GENAI@GHCOPILOT */
}

export function fidoLaunchHandler(launchFIDOAppWindow: (launchData: FIDOAppLaunchData) => void) {
  return (fidoAppScreen: 'auth' | 'register') => {
    // Passing launch data for query params
    launchFIDOAppWindow({
      authDecision: getIsFeatureToggleEnabled('enableNonDcfCvv') ? FIDO : undefined,
      correlationId: getCorrelationId(),
      isCtpDomain: getIsFeatureToggleEnabled('stepupAppDomain'),
      isSPCSupported: getIsSPCSupported(),
      screen: fidoAppScreen
    });
  };
}

export function vppLaunchHandler(launchVPPWindow: (launchData: VPPLaunchData) => void) {
  return (requestPayload: string, checkoutUrl: string, authDecision: AuthenticationDecision) => {
    launchVPPWindow({
      authDecision,
      checkoutUrl,
      codeVerified: getProofState().codeVerifier || '',
      requestPayload,
      sessionId: getSessionId(),
      vppsOrigin: getVppsOrigin()
    });
  };
}

export function launchIssuerOtpFlowHandler(
  launchIssuerOtpApp: (
    authenticationMethod: AuthenticationMethod,
    launchData: IssuerLaunchData
  ) => void
) {
  /* START GENAI@GHCOPILOT */
  const panLastFour = getMaskedCards()?.filter(
    card => card.srcDigitalCardId === getSelectedCardId()
  )[0]?.panLastFour;
  /* END GENAI@GHCOPILOT */
  /* START GENAI@GHCOPILOT */

  return (authenticationMethod: AuthenticationMethod) => {
    launchIssuerOtpApp(authenticationMethod, {
      authDecision: OTP,
      correlationId: getCorrelationId(),
      dpaLocale: getDpaLocale(),
      isCtpDomain: getIsFeatureToggleEnabled('stepupAppDomain'),
      isFidoEligible: isTransactionFidoEligible(),
      merchantCountryCode: getMerchantCountryCode(),
      panLastFour
    });
  };
  /* END GENAI@GHCOPILOT */
}
